import { memo, useState } from 'react';

import classNames from 'classnames';

import { Spinner, useModalController } from '@uikit';

import Share2 from '@uikit/icons/Share2';

import type { HotOffer } from '@shared/api/types/hotOffers';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { HOT_OFFER_TYPE } from '@shared/constants';
import displayErrorToast from '@shared/helpers/displayErrorToast';

import { useReferralUrlQuery } from '@query/user';

import ShareModal from '@store:web/components/common/modals/ShareModal';
import { useHotOfferShareConfig } from '@store:web/components/pages/HotOffersPage/useHotOfferShareConfig';

export interface HotOfferShareButtonProps {
  offer: HotOffer;
}

function HotOfferShareButton({ offer }: HotOfferShareButtonProps) {
  const shareOfferModal = useModalController();
  const authModal = useAuthModal();
  const { isAuth } = useAuth();
  const [shareUrl, setShareUrl] = useState<string>('');
  const {
    id,
    type,
    attributes: { name, cashBackPercentage, offerAmount, offerCurrency },
  } = offer;
  const {
    data,
    refetch: getOfferShareUrl,
    isFetching,
  } = useReferralUrlQuery(id, 'hot-offer', {
    enabled: false,
  });
  const { sharingLink = '' } = data || {};
  const platformConfigurations = useHotOfferShareConfig({
    url: shareUrl,
    name: name ?? '{{OFFER_NAME}}',
    amount: type === HOT_OFFER_TYPE.BINANCE ? String(cashBackPercentage) : offerAmount ?? '0',
    currency: type === HOT_OFFER_TYPE.BINANCE ? '%' : offerCurrency,
  });

  const handleShare = (url: string) => {
    setShareUrl(url);

    shareOfferModal.open();
  };

  const handleClick = async () => {
    if (!isAuth) {
      authModal.open();
      return;
    }

    if (sharingLink) {
      handleShare(sharingLink);
      return;
    }

    const { error, data: { sharingLink: newSharingLink = '' } = {} } = await getOfferShareUrl();

    if (newSharingLink) {
      handleShare(newSharingLink);
    } else if (error) {
      displayErrorToast({ error });
    }
  };

  return (
    <>
      <button
        type="button"
        className={classNames('icon-button button-outline-secondary icon-button-xl', {
          'pointer-events-none': isFetching,
        })}
        onClick={handleClick}
      >
        {isFetching ? <Spinner size="sm" /> : <Share2 className="icon-xs text-secondary-600" />}
      </button>

      <ShareModal
        url={shareUrl}
        modalHeading="Share offer with friends"
        platformConfigurations={platformConfigurations}
        modalController={shareOfferModal}
      />
    </>
  );
}

export default memo(HotOfferShareButton);
